import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAnlageZeile, MsrAspZeile, MsrVdiFunktionsliste } from './typen';
import { SchemaBild } from './SchemaBild';
import { FunktionslisteVdiTabelle } from './FunktionslisteVdiTabelle';
import { PdfKnopf } from './PdfKnopf';
import { ContentSection, EmptyState, ErrorState, Feld, LoadingState } from '../../muster';

/**
 * Reiter „Schemen" — oben die Topologie, darunter Auswahl Anlage → Schema
 * (Umschalter „MSR-Schema (Vorlage)" / „Funktionsschema", Standard
 * MSR-Schema) mit der vollständigen VDI-Funktionsliste Blatt 4.3 darunter
 * (dieselbe Tabelle wie im Reiter Datenpunktliste, Ansicht VDI 3814),
 * Auswahl ASP → ASP-Blatt UND Übersichtsschaltplan (Spezifikation 12.3).
 */
export function Schemen({ api, projektId, anlagen, asps }: {
  api: ApiClient; projektId: string; anlagen: MsrAnlageZeile[]; asps: MsrAspZeile[];
}) {
  const [anlageId, setAnlageId] = useState('');
  const [aspId, setAspId] = useState('');
  const [schemaArt, setSchemaArt] = useState<'msr' | 'funktion'>('msr');
  const [funktionsliste, setFunktionsliste] = useState<MsrVdiFunktionsliste | null>(null);
  const [funktionslisteFehler, setFunktionslisteFehler] = useState<string | null>(null);
  // TM-8c: „Erneut versuchen“ lädt die Funktionsliste neu (vorher setAnlageId mit demselben Wert, ohne Wirkung).
  const [funktionslisteVersuch, setFunktionslisteVersuch] = useState(0);

  useEffect(() => {
    if (!anlageId) { setFunktionsliste(null); return; }

    setFunktionsliste(null);
    setFunktionslisteFehler(null);
    api.msrVdiFunktionsliste(projektId, anlageId)
      .then(setFunktionsliste)
      .catch((e) => setFunktionslisteFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId, anlageId, funktionslisteVersuch]);

  return (
    <>
      <div className="hinweis" style={{ fontSize: 12 }}>
        Aufbau nach VDI 3814 Blatt 4.3 (Funktionsliste und MSR-Schema, Vorgabe
        Teuber + Viel); Übersichtsschaltplan nach Spezifikation 12.3, Referenzkennzeichen
        nach DIN EN 81346.
      </div>

      <ContentSection
        titel="Topologie"
        beschreibung={'Drei Ebenen: Management (GLT, Leitwarte), Automation (ASPs nach '
          + 'Art und Geschoss), Feld (Feldgeräte und Datenpunkte je ASP). Gezeichnet aus '
          + 'dem letzten Ableitungslauf.'}
        aktionen={(
          <PdfKnopf api={api} projektId={projektId} art="topologie" ziel="projekt"
            label="Topologie" />
        )}
      >
        <SchemaBild titel="Topologie" schluessel="topologie"
          laden={() => api.msrSchemaTopologieSvg(projektId)} />
      </ContentSection>

      <ContentSection titel="Schema einer Anlage">
        {/* `breite` wirkt nur im Feldgitter einer `FormSection`; hier steht
            ein einzelnes Feld, das sonst die ganze Breite nimmt. */}
        <div style={{ maxWidth: 380 }}>
          <Feld art="auswahl" beschriftung="Anlage" wert={anlageId}
            optionen={[
              { wert: '', beschriftung: 'Anlage wählen …' },
              ...anlagen.map((a) => ({ wert: a.id, beschriftung: a.kennung })),
            ]}
            beiAenderung={(w) => setAnlageId(w as string)} />
        </div>

        {!anlageId && (
          <EmptyState
            klein
            titel="Keine Anlage gewählt."
            erklaerung={'Mit der Anlage erscheinen ihr MSR-Schema, das Funktionsschema '
              + 'und die GA-Funktionsliste nach VDI 3814 Blatt 4.3.'}
          />
        )}

        {anlageId && (
          <>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <span className="schwach" style={{ fontSize: 12 }}>Ansicht:</span>
              <button type="button" className={`reiter-knopf${schemaArt === 'msr' ? ' aktiv' : ''}`}
                style={{ fontSize: 12 }} onClick={() => setSchemaArt('msr')}>
                MSR-Schema (Vorlage)
              </button>
              <button type="button"
                className={`reiter-knopf${schemaArt === 'funktion' ? ' aktiv' : ''}`}
                style={{ fontSize: 12 }} onClick={() => setSchemaArt('funktion')}>
                Funktionsschema
              </button>
              {schemaArt === 'msr' && (
                <span style={{ marginLeft: 'auto' }}>
                  <PdfKnopf api={api} projektId={projektId} art="schema" ziel="anlage"
                    zielId={anlageId} label="Schema" />
                </span>
              )}
            </div>

            {schemaArt === 'msr'
              ? (
                <SchemaBild titel="MSR-Schema" schluessel={`msr-${anlageId}`}
                  laden={() => api.msrSchemaAnlageSvg(projektId, anlageId)} />
              )
              : (
                <SchemaBild titel="Funktionsschema" schluessel={`funktion-${anlageId}`}
                  laden={() => api.msrSchemaAnlageFunktionsschemaSvg(projektId, anlageId)} />
              )}

            <h4>GA-Funktionsliste VDI 3814 Blatt 4.3</h4>
            {funktionslisteFehler && (
              <ErrorState
                klein
                meldung="Die GA-Funktionsliste dieser Anlage konnte nicht geladen werden."
                technisch={funktionslisteFehler}
                beiErneutVersuchen={() => setFunktionslisteVersuch((v) => v + 1)}
              />
            )}
            {!funktionsliste && !funktionslisteFehler && (
              <LoadingState text="GA-Funktionsliste wird abgerufen …" zeilen={8} spalten={5} />
            )}
            {funktionsliste && <FunktionslisteVdiTabelle f={funktionsliste} />}
          </>
        )}
      </ContentSection>

      <ContentSection titel="ASP-Blatt und Übersichtsschaltplan">
        <div style={{ maxWidth: 380 }}>
          <Feld art="auswahl" beschriftung="Schaltschrank" wert={aspId}
            optionen={[
              { wert: '', beschriftung: 'ASP wählen …' },
              ...asps.map((a) => ({ wert: a.id, beschriftung: a.kennung })),
            ]}
            beiAenderung={(w) => setAspId(w as string)} />
        </div>

        {!aspId && (
          <EmptyState
            klein
            titel="Kein Schaltschrank gewählt."
            erklaerung="Mit dem ASP erscheinen sein Blatt und der Übersichtsschaltplan."
          />
        )}

        {aspId && (
          <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>ASP-Blatt</h4>
              <span style={{ marginLeft: 'auto' }}>
                <PdfKnopf api={api} projektId={projektId} art="aspblatt" ziel="asp" zielId={aspId}
                  label="ASP-Blatt" />
              </span>
            </div>
            <SchemaBild titel="ASP-Blatt" schluessel={aspId}
              laden={() => api.msrSchemaAspSvg(projektId, aspId)} />

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h4 style={{ margin: 0 }}>Übersichtsschaltplan</h4>
              <span style={{ marginLeft: 'auto' }}>
                <PdfKnopf api={api} projektId={projektId} art="schaltplan" ziel="asp" zielId={aspId}
                  label="Schaltplan" />
              </span>
            </div>
            <SchemaBild titel="Übersichtsschaltplan" schluessel={`schaltplan-${aspId}`}
              laden={() => api.msrSchemaAspSchaltplanSvg(projektId, aspId)} />
          </>
        )}
      </ContentSection>
    </>
  );
}
